/**
 * Widget Preloader Utility
 * 
 * Preloads widget chunks in the background after site content API succeeds.
 * This ensures widgets are already downloaded when users navigate to pages,
 * eliminating loading spinners during navigation.
 */

import { widgetImports } from '../widgets/widgetImports';

/**
 * Extract unique widget types from site content
 */
export function extractWidgetTypes(
  widgets: any[] = [],
  pages: Record<string, any> = {}
): string[] {
  const widgetTypes = new Set<string>();
  
  // Extract from widgets array
  widgets.forEach(widget => {
    if (widget?.type) {
      // Handle unique types (e.g., "BasicBanner_ABC123" -> "BasicBanner")
      const baseType = widget.type.includes('_') ? widget.type.split('_')[0] : widget.type;
      widgetTypes.add(baseType);
    }
  });
  
  // Extract from all page widgets
  Object.values(pages).forEach((page: any) => {
    if (page?.widgets && Array.isArray(page.widgets)) {
      page.widgets.forEach((widget: any) => {
        if (widget?.type) {
          const baseType = widget.type.includes('_') ? widget.type.split('_')[0] : widget.type;
          widgetTypes.add(baseType);
        }
      });
    }
  });
  
  return Array.from(widgetTypes);
}

/**
 * Get import function for a widget type from shared imports
 */
function getWidgetImporter(widgetType: string): (() => Promise<any>) | null {
  return widgetImports[widgetType as keyof typeof widgetImports] || null;
}

/**
 * Preload widgets by triggering their dynamic imports
 * Runs in background and handles errors silently
 */
export async function preloadWidgets(widgetTypes: string[]): Promise<void> {
  console.log('📦 Preloading widgets:', widgetTypes);
  
  const preloadPromises = widgetTypes.map(async (widgetType) => {
    try {
      const importer = getWidgetImporter(widgetType);
      
      if (importer) {
        // Trigger the import - this will download the chunk
        await importer();
        console.log(`✅ Preloaded: ${widgetType}`);
      } else {
        console.warn(`⚠️ Widget type not found in registry: ${widgetType}`);
      }
    } catch (error) {
      // Silently handle errors - don't break the app if a widget fails to preload
      console.warn(`❌ Failed to preload widget: ${widgetType}`, error);
    }
  });
  
  // Wait for all preloads to complete (or fail)
  await Promise.allSettled(preloadPromises);
  console.log('✅ Widget preloading complete');
}

/**
 * Main preloader function - extracts widget types and preloads them
 */
export async function preloadRequiredWidgets(
  widgets: any[],
  pages: Record<string, any> = {}
): Promise<void> {
  try {
    console.log('🚀 Starting widget preload process...');
    console.log(`📊 Input stats: ${widgets.length} widgets, ${Object.keys(pages).length} pages`);
    
    // Log each widget
    if (widgets.length > 0) {
      console.log('🎨 Widgets:');
      widgets.forEach((widget, index) => {
        console.log(`  ${index + 1}. Type: ${widget?.type || 'Unknown'}`);
      });
    }
    
    // Log widgets in each page
    const pageKeys = Object.keys(pages);
    if (pageKeys.length > 0) {
      console.log('📄 Page Widgets:');
      pageKeys.forEach((pageSlug) => {
        const page = pages[pageSlug];
        if (page?.widgets && Array.isArray(page.widgets)) {
          console.log(`  Page "${pageSlug}": ${page.widgets.length} widget(s)`);
          page.widgets.forEach((widget: any, index: number) => {
            console.log(`    ${index + 1}. Type: ${widget?.type || 'Unknown'}`);
          });
        }
      });
    }
    
    const widgetTypes = extractWidgetTypes(widgets, pages);
    
    if (widgetTypes.length === 0) {
      console.log('📦 No widgets to preload');
      return;
    }
    
    console.log(`✨ Found ${widgetTypes.length} unique widget types to preload`);
    await preloadWidgets(widgetTypes);
  } catch (error) {
    // Don't throw errors - preloading is a performance optimization, not critical
    console.error('❌ Widget preloading failed:', error);
  }
}
